/**
 * Voting Bar for Gender Party
 * Displays BOY/GIRL voting percentages with animated bars
 */

import { useEffect, useState } from "react";
import type { GenderPartyVoteCounts, GenderVote } from "../hooks";
import styles from "./VotingBar.module.css";

interface VotingBarProps {
  voteCounts: GenderPartyVoteCounts | null;
  myVote?: GenderVote | null;
  revealedAnswer?: GenderVote | null;
  showResults?: boolean;
  animate?: boolean;
  /** "large" for broadcast/projector screens */
  size?: "default" | "large";
  className?: string;
}

export function VotingBar({
  voteCounts,
  myVote,
  revealedAnswer,
  showResults = true,
  animate = true,
  size = "default",
  className = "",
}: VotingBarProps) {
  const [displayedCounts, setDisplayedCounts] =
    useState<GenderPartyVoteCounts | null>(null);

  useEffect(() => {
    if (!animate || !voteCounts) {
      setDisplayedCounts(voteCounts);
      return;
    }

    const startCounts = displayedCounts ?? {
      boyVotes: 0,
      girlVotes: 0,
      totalVotes: 0,
      boyPercentage: 50,
      girlPercentage: 50,
    };

    const duration = 800;
    const startTime = performance.now();

    const animateFrame = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      setDisplayedCounts({
        boyVotes: Math.round(
          startCounts.boyVotes +
            (voteCounts.boyVotes - startCounts.boyVotes) * easeProgress
        ),
        girlVotes: Math.round(
          startCounts.girlVotes +
            (voteCounts.girlVotes - startCounts.girlVotes) * easeProgress
        ),
        totalVotes: Math.round(
          startCounts.totalVotes +
            (voteCounts.totalVotes - startCounts.totalVotes) * easeProgress
        ),
        boyPercentage:
          startCounts.boyPercentage +
          (voteCounts.boyPercentage - startCounts.boyPercentage) * easeProgress,
        girlPercentage:
          startCounts.girlPercentage +
          (voteCounts.girlPercentage - startCounts.girlPercentage) *
            easeProgress,
      });

      if (progress < 1) {
        requestAnimationFrame(animateFrame);
      }
    };

    requestAnimationFrame(animateFrame);
  }, [voteCounts, animate]);

  const counts = displayedCounts ?? {
    boyVotes: 0,
    girlVotes: 0,
    totalVotes: 0,
    boyPercentage: 50,
    girlPercentage: 50,
  };

  const boyIsWinner = revealedAnswer === "BOY";
  const girlIsWinner = revealedAnswer === "GIRL";

  return (
    <div
      className={`${styles.container} ${size === "large" ? styles.large : ""} ${className}`}
      role="group"
      aria-label="Результаты голосования"
    >
      <div className={styles.barContainer}>
        <div className={styles.labels}>
          <div
            className={`${styles.label} ${styles.boyLabel} ${
              myVote === "BOY" ? styles.myVote : ""
            } ${boyIsWinner ? styles.winner : ""}`}
          >
            <span className={styles.emoji}>👦</span>
            <span className={styles.text}>Мальчик</span>
            {showResults && (
              <span className={styles.percentage}>
                {Math.round(counts.boyPercentage)}%
              </span>
            )}
          </div>

          <div
            className={`${styles.label} ${styles.girlLabel} ${
              myVote === "GIRL" ? styles.myVote : ""
            } ${girlIsWinner ? styles.winner : ""}`}
          >
            {showResults && (
              <span className={styles.percentage}>
                {Math.round(counts.girlPercentage)}%
              </span>
            )}
            <span className={styles.text}>Девочка</span>
            <span className={styles.emoji}>👧</span>
          </div>
        </div>

        <div
          className={styles.bar}
          role="progressbar"
          aria-valuenow={showResults ? Math.round(counts.boyPercentage) : 50}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Мальчик ${Math.round(counts.boyPercentage)}%, Девочка ${Math.round(counts.girlPercentage)}%`}
        >
          <div
            className={`${styles.boyBar} ${boyIsWinner ? styles.winnerBar : ""}`}
            style={{
              width: showResults ? `${counts.boyPercentage}%` : "50%",
            }}
          />
          <div
            className={`${styles.girlBar} ${girlIsWinner ? styles.winnerBar : ""}`}
            style={{
              width: showResults ? `${counts.girlPercentage}%` : "50%",
            }}
          />
        </div>

        {showResults && (
          <div className={styles.votes}>
            <span className={styles.boyVotes}>{counts.boyVotes} голосов</span>
            <span className={styles.totalVotes}>
              Всего: {counts.totalVotes}
            </span>
            <span className={styles.girlVotes}>{counts.girlVotes} голосов</span>
          </div>
        )}
      </div>

      {revealedAnswer && (
        <div
          className={`${styles.answerReveal} ${
            boyIsWinner ? styles.boyWinner : styles.girlWinner
          }`}
        >
          <span className={styles.answerEmoji}>
            {boyIsWinner ? "👦" : "👧"}
          </span>
          <span className={styles.answerText}>
            Это {boyIsWinner ? "мальчик" : "девочка"}!
          </span>
        </div>
      )}
    </div>
  );
}
